import { useState, useEffect } from 'react';
import { useTheme } from '../../contexts/ThemeContext';
import AdminLayout from '../../components/AdminLayout';
import { Button } from '../../components/ui/button';
import { Textarea } from '../../components/ui/textarea';
import { toast } from 'sonner';
import axios from 'axios';
import { 
  MessageSquare, 
  Send,
  Mail,
  Clock,
  CheckCheck
} from 'lucide-react';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

export default function AdminMessages() {
  const { currentTheme } = useTheme();
  const [messages, setMessages] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedMessage, setSelectedMessage] = useState(null);
  const [replyContent, setReplyContent] = useState('');
  const [sending, setSending] = useState(false);

  useEffect(() => {
    fetchMessages();
  }, []);

  const fetchMessages = async () => {
    try {
      const response = await axios.get(`${API}/admin/messages`, { withCredentials: true });
      setMessages(response.data || []);
    } catch (error) {
      console.error('Error fetching messages:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleReply = async (messageId) => {
    if (!replyContent.trim()) return;
    setSending(true);

    try {
      await axios.post(`${API}/messages/${messageId}/reply`, 
        { subject: '', content: replyContent },
        { withCredentials: true }
      );
      toast.success('Reply sent!');
      setReplyContent('');
      fetchMessages();
    } catch (error) {
      toast.error('Failed to send reply');
    } finally {
      setSending(false);
    }
  };

  const markAsRead = async (messageId) => {
    try {
      await axios.put(`${API}/messages/${messageId}/read`, {}, { withCredentials: true });
      fetchMessages();
    } catch (error) {
      console.error('Error marking as read:', error);
    }
  };

  const formatDate = (dateString) => {
    return new Date(dateString).toLocaleString();
  };

  // Filter to show only user messages to admin (recipient_id is null)
  const userMessages = messages.filter(m => !m.reply_to && m.recipient_id === null);

  return (
    <AdminLayout title="Messages">
      <div className="grid md:grid-cols-3 gap-6">
        {/* Message List */}
        <div 
          className="md:col-span-1 rounded-2xl overflow-hidden"
          style={{ backgroundColor: 'white', boxShadow: '0 4px 20px rgba(0,0,0,0.05)' }}
        >
          <div 
            className="p-4 border-b font-body font-medium"
            style={{ borderColor: currentTheme.border }}
          >
            User Messages ({userMessages.filter(m => !m.is_read).length} unread)
          </div>
          <div className="divide-y max-h-[600px] overflow-y-auto">
            {loading ? (
              <div className="p-4 space-y-4">
                {[1, 2, 3].map((i) => (
                  <div key={i} className="skeleton h-16 rounded-xl" />
                ))}
              </div>
            ) : userMessages.length > 0 ? (
              userMessages.map((message) => (
                <button
                  key={message.message_id}
                  onClick={() => {
                    setSelectedMessage(message);
                    if (!message.is_read) markAsRead(message.message_id);
                  }}
                  className={`w-full p-4 text-left transition-colors hover:bg-gray-50 ${
                    selectedMessage?.message_id === message.message_id ? 'bg-gray-50' : ''
                  }`}
                  data-testid={`admin-message-${message.message_id}`}
                >
                  <div className="flex items-start gap-3">
                    <div 
                      className="w-10 h-10 rounded-full flex items-center justify-center flex-shrink-0"
                      style={{ backgroundColor: `${currentTheme.primary}20` }}
                    >
                      <span style={{ color: currentTheme.primary }}>
                        {message.sender_name?.charAt(0)}
                      </span>
                    </div>
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center justify-between gap-2">
                        <span className={`font-body ${!message.is_read ? 'font-semibold' : ''}`}>
                          {message.sender_name}
                        </span>
                        {!message.is_read && (
                          <span 
                            className="w-2 h-2 rounded-full"
                            style={{ backgroundColor: currentTheme.primary }}
                          />
                        )}
                      </div>
                      <p className="font-body text-sm font-medium truncate">{message.subject}</p>
                      <p className="font-body text-xs opacity-60">{formatDate(message.created_at)}</p>
                    </div>
                  </div>
                </button>
              ))
            ) : (
              <div className="p-8 text-center">
                <Mail className="w-12 h-12 mx-auto mb-3 opacity-30" />
                <p className="font-body opacity-60">No messages</p>
              </div>
            )}
          </div>
        </div>

        {/* Message Detail */}
        <div 
          className="md:col-span-2 rounded-2xl"
          style={{ backgroundColor: 'white', boxShadow: '0 4px 20px rgba(0,0,0,0.05)' }}
        >
          {selectedMessage ? (
            <div className="h-full flex flex-col">
              <div className="p-6 border-b" style={{ borderColor: currentTheme.border }}>
                <h2 className="font-display text-xl font-medium mb-2">{selectedMessage.subject}</h2>
                <div className="flex items-center gap-4 text-sm opacity-60">
                  <span>From: {selectedMessage.sender_name} (@{selectedMessage.sender_username})</span>
                  <span className="flex items-center gap-1">
                    <Clock className="w-4 h-4" />
                    {formatDate(selectedMessage.created_at)}
                  </span>
                </div>
              </div>

              <div className="flex-1 p-6 overflow-y-auto space-y-4 max-h-[400px]">
                <div 
                  className="rounded-2xl p-4"
                  style={{ backgroundColor: currentTheme.muted }}
                >
                  <p className="font-body whitespace-pre-wrap">{selectedMessage.content}</p>
                </div>

                {/* Replies */}
                {messages
                  .filter(m => m.reply_to === selectedMessage.message_id)
                  .map((reply) => (
                    <div
                      key={reply.message_id}
                      className={`rounded-2xl p-4 ${
                        reply.sender_username === 'admin' ? 'ml-8' : ''
                      }`}
                      style={{ 
                        backgroundColor: reply.sender_username === 'admin' 
                          ? `${currentTheme.primary}10` 
                          : currentTheme.muted 
                      }}
                    >
                      <div className="flex items-center gap-2 mb-2 text-sm">
                        <span className="font-medium">{reply.sender_name}</span>
                        <span className="opacity-60">{formatDate(reply.created_at)}</span>
                      </div>
                      <p className="font-body whitespace-pre-wrap">{reply.content}</p>
                    </div>
                  ))}
              </div>

              {/* Reply Input */}
              <div className="p-4 border-t" style={{ borderColor: currentTheme.border }}>
                <div className="flex gap-3">
                  <Textarea
                    placeholder="Type your reply..."
                    value={replyContent}
                    onChange={(e) => setReplyContent(e.target.value)}
                    rows={2}
                    className="flex-1"
                    data-testid="admin-reply-input"
                  />
                  <Button
                    onClick={() => handleReply(selectedMessage.message_id)}
                    disabled={!replyContent.trim() || sending}
                    style={{ backgroundColor: currentTheme.primary }}
                    data-testid="admin-send-reply"
                  >
                    <Send className="w-4 h-4" />
                  </Button>
                </div>
              </div>
            </div>
          ) : (
            <div className="h-full flex items-center justify-center p-12">
              <div className="text-center">
                <MessageSquare className="w-16 h-16 mx-auto mb-4 opacity-20" />
                <h3 className="font-display text-xl font-medium mb-2">Select a message</h3>
                <p className="font-body opacity-60">Choose a message to view and reply</p>
              </div>
            </div>
          )}
        </div>
      </div>
    </AdminLayout>
  );
}
